// Imágenes de las prendas en el depósito privado "prendas" de Supabase Storage
// (ver supabase/migrations/20261002180000_imagenes_prendas.sql y 20261002190000_imagenes_originales.sql).
import type { SupabaseClient } from '@supabase/supabase-js';

const DEPOSITO = 'prendas';
export const MAX_IMAGENES = 3;
export const MAX_MB = 50;
const EXT: Record<string, string> = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic', 'image/heif': 'heif' };
const TIPO: Record<string, string> = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', heif: 'image/heif' };

// Cada imagen se guarda dos veces: el original tal como llegó (para vectorizar y diseñar) y una vista reducida para
// verla rápido en el sistema. La prenda guarda la ruta del original; la de la vista sale de ella.
export const vistaDe = (ruta: string) => 'vistas/' + ruta.replace(/\.[^./]+$/, '') + '.jpg';

// Vista: lado mayor de hasta 1600 px, en JPEG. El fondo transparente (PNG) queda blanco.
export async function reducirImagen(archivo: Blob, max = 1600, calidad = 0.85): Promise<Blob> {
  const url = URL.createObjectURL(archivo);
  try {
    const img = await new Promise<HTMLImageElement>((ok, mal) => { const i = new Image(); i.onload = () => ok(i); i.onerror = () => mal(new Error('formato')); i.src = url; });
    const escala = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.round(img.naturalWidth * escala); c.height = Math.round(img.naturalHeight * escala);
    const x = c.getContext('2d')!;
    x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
    x.drawImage(img, 0, 0, c.width, c.height);
    return await new Promise<Blob>((ok, mal) => c.toBlob(b => (b ? ok(b) : mal(new Error('formato'))), 'image/jpeg', calidad));
  } finally { URL.revokeObjectURL(url); }
}

// Sube el original y su vista; devuelve la ruta del original (año/mes/nombre aleatorio.extensión).
export async function subirImagen(sb: SupabaseClient, archivo: File): Promise<string> {
  const ext = (archivo.name.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase(), tipo = TIPO[ext] || archivo.type;
  if (!EXT[tipo]) throw new Error(`"${archivo.name}" no es una foto JPG, PNG, WEBP o HEIC.`);
  if (archivo.size > MAX_MB * 1048576) throw new Error(`"${archivo.name}" pesa ${Math.round(archivo.size / 1048576)} MB; el máximo es ${MAX_MB} MB.`);
  let vista: Blob;
  try { vista = await reducirImagen(archivo); }
  catch { throw new Error(`Este navegador no puede abrir "${archivo.name}". Si es una foto HEIC del iPhone, súbela desde Safari o compártela como JPG.`); }
  const hoy = new Date(), ruta = `${hoy.getFullYear()}/${String(hoy.getMonth() + 1).padStart(2, '0')}/${crypto.randomUUID()}.${EXT[tipo]}`;
  const dep = sb.storage.from(DEPOSITO);
  const r1 = await dep.upload(ruta, await archivo.arrayBuffer(), { contentType: tipo, upsert: false });
  if (r1.error) throw new Error('No se pudo subir la imagen: ' + r1.error.message);
  const r2 = await dep.upload(vistaDe(ruta), await vista.arrayBuffer(), { contentType: 'image/jpeg', upsert: false });
  if (r2.error) throw new Error('No se pudo subir la imagen: ' + r2.error.message);
  return ruta;
}

// Enlaces firmados (vencen en una hora); se guardan en memoria para no pedirlos de nuevo en cada pantalla.
// Una ruta que no existe (por ejemplo, la vista de una imagen subida antes de guardar originales) queda sin enlace.
const firmados = new Map<string, { url: string; vence: number }>();
export async function enlacesDe(sb: SupabaseClient, rutas: string[]): Promise<Map<string, string>> {
  const ahora = Date.now(), faltan = [...new Set(rutas)].filter(r => (firmados.get(r)?.vence || 0) < ahora + 60_000);
  if (faltan.length) {
    const { data, error } = await sb.storage.from(DEPOSITO).createSignedUrls(faltan, 3600);
    if (error) throw new Error(error.message);
    for (const d of data || []) if (d.path && d.signedUrl) firmados.set(d.path, { url: d.signedUrl, vence: ahora + 3600_000 });
  }
  return new Map(rutas.map(r => [r, firmados.get(r)?.url || '']));
}

// Enlace que descarga el original con un nombre claro (Supabase lo entrega como archivo adjunto con ?download=).
export const enlaceDescarga = (url: string, nombre: string) => url + (url.includes('?') ? '&' : '?') + 'download=' + encodeURIComponent(nombre);
